import type {KeyboardEvent, ReactElement, Ref} from 'react';
import React, {useRef} from 'react';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {DEFAULT_NUMBER_OF_ROWS} from '@Components/input-field-v2/input-field.types';
import styles from './subtitle-box-text-area.module.scss';
import type {SubtitleBoxInputFieldProps} from './subtitle-box-text-area.types';

const typingStoppedTimeout = 600;

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SubtitleBoxTextArea = React.forwardRef(
  (
    {
      id = '',
      input = '',
      className = '',
      inputClassName = '',
      onInputBlur = (): void => {},
      onInputFocus = (): void => {},
      onInputSubmit = (): void => {},
      onInputChange = (): void => {},
      onInputChangeEnded = (): void => {},
      onArrowUpKeyDown = (): void => {},
      onArrowDownKeyDown = (): void => {},
      onBackSpaceKeyDown = (): void => {},
      maxNumberOfRows = DEFAULT_NUMBER_OF_ROWS,
    }: SubtitleBoxInputFieldProps,
    ref: Ref<HTMLTextAreaElement>
  ) => {
    const timer = useRef<number | undefined>(undefined);

    const getInputClasses = (): string => {
      const classes = [inputClassName, 'hover-transition-all', styles.input, getInputPadding(), 'body-md-regular'];

      classes.push(styles.textArea);

      return classes.join(' ');
    };

    const getInputPadding = (): string => {
      let classes = '';
      classes = `${styles.paddingLeftSmall} ${styles.paddingTopAndBottomSmall} `;

      classes += styles.paddingRightSmall;

      return classes;
    };

    const getContainerClassesForParentContainer = (): string => {
      return [className, 'flex-row-align-center', styles.parentContainer].join(' ');
    };

    const handleTypingStoppedHandler = (value: InputType): void => {
      if (timer) {
        clearTimeout(timer.current);
      }

      timer.current = window.setTimeout(() => {
        onInputChangeEnded(value);
      }, typingStoppedTimeout);
    };

    const handleOnKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter') {
        if (timer.current) {
          clearTimeout(timer.current);
        }
        onInputSubmit();
        e.currentTarget.blur();

        e.preventDefault();
      }
      if (e.key === 'ArrowUp') {
        onArrowUpKeyDown(e);
      }
      if (e.key === 'Backspace') {
        const target = e.target as HTMLTextAreaElement;
        if (target.selectionStart !== undefined && target.selectionStart === 0) {
          if (timer.current) {
            clearTimeout(timer.current);
          }
        }
        onBackSpaceKeyDown(e);
      }
      if (e.key === 'ArrowDown') {
        onArrowDownKeyDown();
      }
      // if (e.key === 'Tab') {
      //   onInputBlur(e.currentTarget.value);
      // }
    };

    const getTextArea = (): ReactElement<any> => {
      return (
        <textarea
          ref={ref}
          id={id}
          placeholder=""
          className={getInputClasses()}
          value={input}
          onBlur={(e): void => {
            onInputBlur(e.target.value);
          }}
          onFocus={(e): void => {
            onInputFocus(e.target.value);
          }}
          onChange={(e): void => {
            maintainCursorPoisition(e);
            onInputChange(e.target.value);

            handleTypingStoppedHandler(e.target.value);
          }}
          onKeyDown={handleOnKeyDown}
          rows={maxNumberOfRows}
        />
      );
    };

    const maintainCursorPoisition = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const caret = e.target.selectionStart;
      const element = e.target;
      window.requestAnimationFrame(() => {
        element.selectionStart = caret;
        element.selectionEnd = caret;
      });
    };

    return <div className={getContainerClassesForParentContainer()}>{getTextArea()}</div>;
  }
);
